/**
 * Helper functions for entry fee formatting and calculations.
 *
 * Task 15.2: POL/USD amounts + Prize Pool
 */

import { EntryFee } from '../../../types/tournament';

/**
 * Format POL amount for display
 * @param amount - Amount as string (from EntryFee.amount)
 * @returns Formatted string, e.g., "50 POL", "1,000 POL"
 */
export const formatAmountPOL = (amount: string): string => {
  const num = parseFloat(amount);
  if (!Number.isFinite(num)) return '0 POL';
  return `${num.toLocaleString('en-US', { maximumFractionDigits: 2 })} POL`;
};

/**
 * Format USD equivalent for display
 * @param polAmount - Amount in POL (string)
 * @param polPriceUsd - Current POL price in USD
 * @returns Formatted string, e.g., "~$12.50 USD", or null if price unavailable
 */
export const formatAmountUSD = (
  polAmount: string,
  polPriceUsd: number | null
): string | null => {
  // Treat null, undefined, or 0 as unavailable
  if (polPriceUsd == null || polPriceUsd === 0) return null;

  const polNum = parseFloat(polAmount);
  if (!Number.isFinite(polNum)) return null;

  const usdValue = polNum * polPriceUsd;
  return `~$${parseFloat(usdValue.toFixed(4))} USD`;
};

/**
 * Calculate estimated prize pool
 * Formula: entryFee.amount × usersLimit × fillRate
 *
 * @param entryFee - EntryFee object
 * @param usersLimit - Maximum participants
 * @param fillRate - Expected fill rate (default 0.8 = 80%)
 * @returns Estimated prize pool in POL, or null if cannot calculate
 */
export const calculateEstimatedPrizePool = (
  entryFee: EntryFee | undefined,
  usersLimit: number | undefined,
  fillRate: number = 0.8
): number | null => {
  // Cannot calculate for FREE tournaments
  if (!entryFee || entryFee.type === 'FREE') return null;

  // Cannot calculate without valid usersLimit
  if (usersLimit == null || usersLimit <= 0) return null;

  const amount = parseFloat(entryFee.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  return amount * usersLimit * fillRate;
};

/**
 * Format prize pool for display
 * Uses K/M suffixes for large values
 *
 * @param prizePool - Prize pool amount in POL
 * @returns Formatted string, e.g., "500 POL", "5.0K POL", "2.5M POL"
 */
export const formatPrizePool = (prizePool: number): string => {
  if (prizePool >= 1_000_000) {
    return `${(prizePool / 1_000_000).toFixed(1)}M POL`;
  }
  if (prizePool >= 1_000) {
    return `${(prizePool / 1_000).toFixed(1)}K POL`;
  }
  return `${prizePool.toLocaleString('en-US', { maximumFractionDigits: 0 })} POL`;
};
